/**
 * ModerationCaseCard Component
 *
 * Container component for displaying moderation case information.
 * Provides a rich, card-based interface that supports multi-content display.
 *
 * @example
 * ```tsx
 * <ModerationCaseCard>
 *   <ModerationCaseCardHeader
 *     contentTypes={[...]}
 *     segmentName="Screenshots"
 *     username="user123"
 *     timestamp="1/21/2026"
 *   />
 *
 *   <ModerationCaseCardSection icon={Chat} title="Text" count={3}>
 *     <TextContent messages={messages} />
 *   </ModerationCaseCardSection>
 *
 *   <ModerationCaseCardSection icon={Photo} title="Image" count={4}>
 *     <ImageGallery images={images} />
 *   </ModerationCaseCardSection>
 * </ModerationCaseCard>
 * ```
 */

import { Card } from '@soteria/design-system-ve';
import { forwardRef } from 'react';
import type { ReactNode, Ref } from 'react';

import * as styles from './ModerationCaseCard.css';

export interface ModerationCaseCardProps {
  /**
   * Child components (Header, Sections)
   */
  children: ReactNode;

  /**
   * Additional CSS class name
   */
  className?: string;

  /**
   * Whether this card is the focused/selected card in the multi-card view.
   * Applies a visual highlight border when true.
   */
  isFocused?: boolean;
}

/**
 * Main ModerationCaseCard component
 *
 * Container for moderation case information with header and content sections.
 */
function ModerationCaseCardInner({ children, className, isFocused = false }: ModerationCaseCardProps, ref: Ref<HTMLDivElement>) {
  const combinedClassName = [styles.cardContainer, isFocused && styles.cardFocused, className].filter(c => c).join(' ');

  return (
    <Card ref={ref} className={combinedClassName} elevation="low" padding="medium">
      {children}
    </Card>
  );
}

/**
 * Export ModerationCaseCard with forwardRef support
 */
export const ModerationCaseCard = forwardRef(ModerationCaseCardInner);

ModerationCaseCard.displayName = 'ModerationCaseCard';
